import { useEffect } from 'react'
import { useQuery } from 'convex/react'
import { Link, useParams } from 'react-router-dom'
import { MessageSquare, SearchX, ShieldAlert } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import { useChatPanel } from '@/components/chat/ChatContext'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Loading } from '@/components/ui/loading'

export function PrimoupChatDeepLink() {
  const { externalPrescriptionId } = useParams<{
    externalPrescriptionId: string
  }>()
  const { openChat } = useChatPanel()
  const resolved = useQuery(
    api.prescriptionChat.resolvePrimoupChatLink,
    externalPrescriptionId
      ? { external_prescription_id: externalPrescriptionId }
      : 'skip'
  )

  useEffect(() => {
    if (resolved?.status === 'ok') {
      openChat(resolved.prescription_id)
    }
  }, [resolved, openChat])

  if (!externalPrescriptionId || resolved?.status === 'not_found') {
    return (
      <DeepLinkState
        icon={<SearchX className="h-7 w-7 text-amber-600" />}
        title="Chat non trovata"
        description="Non esiste una prescrizione attiva collegata a questo identificativo PrimoUp. Verifica il link nell'app dei tecnici."
      />
    )
  }

  if (resolved === undefined) {
    return <Loading text="Apertura della chat..." />
  }

  if (resolved.status === 'forbidden') {
    return (
      <DeepLinkState
        icon={<ShieldAlert className="h-7 w-7 text-red-600" />}
        title="Accesso alla chat non consentito"
        description="Il tuo account non è abilitato a questa prescrizione. Accedi con l'account tecnico corretto oppure chiedi all'amministratore di verificare ruolo e permessi."
      />
    )
  }

  return (
    <DeepLinkState
      icon={<MessageSquare className="h-7 w-7 text-primary" />}
      title="Conversazione aperta"
      description={`${resolved.patient_name} · prescrizione n. ${resolved.numero_prescrizione}`}
      action={
        <Button onClick={() => openChat(resolved.prescription_id)}>
          <MessageSquare className="mr-2 h-4 w-4" />
          Riapri chat
        </Button>
      }
    />
  )
}

function DeepLinkState({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode
  title: string
  description: string
  action?: React.ReactNode
}) {
  return (
    <div className="mx-auto flex min-h-[55vh] max-w-xl items-center justify-center py-8">
      <Card className="w-full">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
            {icon}
          </div>
          <CardTitle>{title}</CardTitle>
          <CardDescription className="max-w-md leading-relaxed">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center gap-3">
          {action}
          <Button asChild variant="outline">
            <Link to="/">Torna alla home</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
